import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Text, useTheme } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import MobileEmailPreview from '../MobileEmailPreview';
import TextareaWithFieldInsert from '../TextareaWithFieldInsert';
import { formatCampaignReplaceInsert } from '../formatCampaignReplaceInsert';

import './styles.scss';

const SubjectForm = memo(
    ({
        subject,
        previewText,
        senderDisplayName,
        onSubjectChange,
        onPreviewTextChange,
        onCancel,
        onSave,
    }) => {
        const theme = useTheme();

        const handleSubjectChange = useCallback(
            (value) => {
                onSubjectChange?.(value);
            },
            [onSubjectChange],
        );

        const handlePreviewTextChange = useCallback(
            (value) => {
                onPreviewTextChange?.(value);
            },
            [onPreviewTextChange],
        );

        const handleCancel = useCallback(() => {
            onCancel?.();
        }, [onCancel]);

        const handleSave = useCallback(() => {
            onSave?.();
        }, [onSave]);

        const subjectValue = useMemo(() => subject || '', [subject]);
        const previewTextValue = useMemo(() => previewText || '', [previewText]);
        const isValid = useMemo(() => subjectValue.trim().length > 0, [subjectValue]);

        return (
            <div className="subject-form">
                <div className="subject-form__content">
                    <div className="subject-form__fields">
                        <TextareaWithFieldInsert
                            value={subjectValue}
                            onChange={handleSubjectChange}
                            label={getLiteral('label_subject')}
                            hint={getLiteral('label_marketing_campaigns_subject_info')}
                            isRequired
                            minRows={3}
                            useCampaignReplaceVariables
                            formatInsertedVariable={formatCampaignReplaceInsert}
                        />
                        <TextareaWithFieldInsert
                            value={previewTextValue}
                            onChange={handlePreviewTextChange}
                            label={getLiteral('label_preview_text')}
                            hint={getLiteral('label_marketing_campaigns_preview_text_info')}
                            minRows={3}
                            useCampaignReplaceVariables
                            formatInsertedVariable={formatCampaignReplaceInsert}
                        />
                    </div>

                    <div className="subject-form__preview">
                        <MobileEmailPreview
                            name={senderDisplayName}
                            message={subjectValue || 'Message subject...'}
                            previewText={previewTextValue || getLiteral('label_preview_text')}
                            variant="large"
                        />
                        <Text
                            type="caption"
                            color={theme.colors.textLight.secondary}
                            className="subject-form__preview-note"
                        >
                            {getLiteral('label_marketing_campaigns_email_preview_may_vary')}
                        </Text>
                    </div>
                </div>

                <div className="subject-form__actions">
                    <Button type="secondary" size="small" onClick={handleCancel}>
                        {getLiteral('action_cancel')}
                    </Button>
                    <Button type="primary" size="small" onClick={handleSave} isDisabled={!isValid}>
                        {getLiteral('action_save')}
                    </Button>
                </div>
            </div>
        );
    },
);

SubjectForm.displayName = 'SubjectForm';

SubjectForm.propTypes = {
    subject: PropTypes.string,
    previewText: PropTypes.string,
    senderDisplayName: PropTypes.string,
    onSubjectChange: PropTypes.func,
    onPreviewTextChange: PropTypes.func,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
};

export default SubjectForm;
